import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import EmailVariablePicker from "./EmailVariablePicker";
import Editor from "../../components/Editor";
import ToolbarItemPicker from "./ToolbarItemPicker";
import { getItem } from "../../api/ProjectApi";
import MessageOverview from "../MessageOverview";
import EmailSnippetPicker from "./EmailSnippetPicker";

import RecipientsSidebar from "./RecipientsSidebar";
import RecipientsSelect from "../../components/RecipientsSelect";
import SubjectVariablePicker from "./SubjectVariablePicker";
import EmailCanvaPicker from "./EmailCanvaPicker";

const EmailForm = ({
  isEditMode,
  appGoogleData,
  appAzureData,
  projectId,
  eventData,
  onDataChange,
  validationErrors,
  editorInputDescriptionRef,
  onCreateSnippet,
  onManageSnippets,
  massSending = false,
  navigateToUrl,
}) => {
  const { t } = useTranslation();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const variablePickerOpenRef = useRef<(() => void) | null>(null);
  const canvaPickerOpenRef = useRef<(() => void) | null>(null);

  const hasGoogleCalendar = appGoogleData.getIn(["data", "googleSetting", "calendar"]) || false;
  const hasGmail =
    (appGoogleData.get("connected") || false) &&
    (appGoogleData.getIn(["data", "googleSetting", "gmail"]) || false);
  const hasOutlook = appAzureData.get("connected") || false;
  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const currentUserEmail = currentUser.get("email");
  const members = useSelector((state: any) => state.projectReducer.getIn(["project", "members"]));
  const attributes = useSelector((state: any) => state.template.get("attributes"));

  const fallbackEmail = currentUserEmail || appGoogleData.get("email") || appAzureData.get("email");

  useEffect(() => {
    if (!isEditMode) {
      const attendees = eventData.attendees || [];
      if (fallbackEmail) {
        if (!attendees.includes(fallbackEmail)) {
          attendees.push(fallbackEmail);
        }
        onDataChange("organizer", fallbackEmail);
      }
      if (eventData.contactId) {
        getItem(eventData.contactId)
          .then((data) => {
            const item = fromJS(data);
            if (data && item.getIn(["data", "email", "value"])) {
              attendees.push(item.getIn(["data", "email", "value"]));
            }
            onDataChange("attendees", attendees);
          })
          .catch(() => {
            onDataChange("attendees", attendees);
          });
      } else {
        onDataChange("attendees", attendees);
      }
    }
  }, [fallbackEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEditMode) {
      onDataChange("hasGmail", hasGmail);
      onDataChange("hasGoogleCalendar", hasGoogleCalendar);
    }
  }, [hasGmail, hasGoogleCalendar]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEditMode) {
      onDataChange("hasOutlook", hasOutlook);
    }
  }, [hasOutlook]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeField = (fieldId, fieldValue) => {
    onDataChange(fieldId, fieldValue);
  };

  const handleApplySnippet = (snippet) => {
    if (snippet.subject) handleChangeField("summary", snippet.subject);
    if (snippet.description && editorInputDescriptionRef?.current) {
      editorInputDescriptionRef.current.root.innerHTML = snippet.description;
      handleChangeField("description", snippet.description);
    }
  };

  const handleSelectSearchItem = (searchItem) => {
    const item = {
      value: searchItem.get("id"),
      label: searchItem.getIn(["data", "name", "value"]),
      type: searchItem.get("type"),
    };
    handleChangeField("item", item);
    if (searchItem.get("type") !== "SALE") {
      const attendeeEmail = searchItem.getIn(["data", "email", "value"]);
      const attendees = [...eventData.attendees];
      if (attendeeEmail && !attendees.includes(attendeeEmail)) {
        attendees.push(attendeeEmail);
      }
      handleChangeField("attendees", attendees);
    } else {
      const contactId =
        searchItem.getIn(["data", "contact", "value", "id"]) ||
        searchItem.getIn(["data", "contact", "value", "value"]);
      handleChangeField("contactId", contactId);
    }
  };

  // Edit mode: read-only view
  if (isEditMode) {
    return (
      <div className="min-w-2xl">
        <MessageOverview eventData={eventData} />
      </div>
    );
  }

  // Compose mode: 2-column split layout
  return (
    <div className="flex w-full h-full min-h-[480px]">
      {/* ── LEFT PANE: Compose area ── */}
      <div className="flex-1 flex flex-col gap-4 p-4 min-w-0">
        {/* From row */}
        <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
          <span className="flex-none w-16 text-xs font-semibold text-gray-400 uppercase tracking-wide">
            {t("From")}
          </span>
          <div className="flex items-center gap-2">
            <img
              src={currentUser.get("avatarUrl")}
              alt={currentUser.get("displayName")}
              className="w-6 h-6 rounded-full object-cover"
            />
            <span className="text-sm font-medium text-gray-900">
              {currentUser.get("displayName") || currentUserEmail}
            </span>
            {currentUser.get("displayName") && (
              <span className="text-xs text-gray-500">{currentUserEmail}</span>
            )}
          </div>
        </div>

        {/* To row — shows count + links to sidebar */}
        {(() => {
          const itemIdCount = eventData.itemIds
            ? eventData.itemIds.split(",").filter(Boolean).length
            : 0;
          const attendeeCount = eventData.attendees?.length ?? 0;
          const totalCount = itemIdCount + attendeeCount;
          return (
            <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
              <span className="flex-none w-16 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                {t("To")}
              </span>
              <div className="flex items-center gap-2 flex-1">
                {massSending ? (
                  <span className="text-sm text-gray-600 flex items-center gap-1.5">
                    <svg
                      className="w-3.5 h-3.5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    {t("Sending individual emails to {{count}} recipients", { count: totalCount })}
                  </span>
                ) : (
                  <div className="w-full flex items-center h-8">
                    {itemIdCount > 0 && (
                      <div className="text-sm text-gray-600 mr-2 shrink-0 flex items-center">
                        <span className="font-medium bg-gray-100 px-2 py-1 rounded-md">
                          {t("{{count}} selected item(s)", { count: itemIdCount })}
                        </span>
                        <span className="ml-2 text-gray-400">+</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <RecipientsSelect
                        id="to"
                        projectId={projectId}
                        value={eventData.attendees || []}
                        onChange={(value) => handleChangeField("attendees", value)}
                        placeholder={t("Add recipients...")}
                      />
                    </div>
                  </div>
                )}
              </div>
              {validationErrors.attendees && (
                <p className="text-xs text-red-600">{validationErrors.attendees}</p>
              )}
            </div>
          );
        })()}

        {/* Subject field with variable picker */}
        <div className="flex items-start gap-3 pb-3 border-b border-gray-100">
          <span className="flex-none w-16 pt-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">
            {t("Subject")}
          </span>
          <div className="flex-1">
            <SubjectVariablePicker
              value={eventData.summary}
              onChange={(value) => handleChangeField("summary", value)}
              placeholder={t("Enter subject... (type { to add variable)")}
              errorMessage={validationErrors.summary}
            />
          </div>
        </div>

        {/* Content editor */}
        <div className="flex flex-col gap-2 flex-1">
          {/* Content label only */}
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
            {t("Content")}
          </span>

          <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm [&_.ql-toolbar]:!border-0 [&_.ql-toolbar]:!padding-0 [&_.ql-container]:!border-0 [&_.ql-editor]:min-h-[280px] [&_.ql-toolbar_button:not([class*='ql-'])]:!w-auto">
            {/* Toolbar */}
            <div
              ref={toolbarRef}
              className={`flex items-center flex-wrap gap-1 px-3 py-2 bg-gray-50 border-b border-gray-200`}
            >
              <ToolbarItemPicker
                selectedItem={eventData.item}
                onSelectItem={(searchItem) => {
                  if (searchItem.get("id")) {
                    handleSelectSearchItem(searchItem);
                  } else {
                    handleChangeField("item", { type: searchItem.get("type") });
                  }
                }}
              />
              {validationErrors.linkItem && (
                <p className="text-xs text-red-600">{validationErrors.linkItem}</p>
              )}
              <div className="w-px h-4 bg-gray-300 mx-0.5" />
              <EmailSnippetPicker
                iconOnly
                onSelect={handleApplySnippet}
                onCreateSnippet={onCreateSnippet}
                onManageSnippets={onManageSnippets}
              />
              <EmailVariablePicker
                quillRef={editorInputDescriptionRef}
                openRef={variablePickerOpenRef}
              />
              <div className="w-px h-4 bg-gray-300 mx-0.5" />
              <span className="ql-formats">
                <button className="ql-bold" />
                <button className="ql-italic" />
                <button className="ql-underline" />
              </span>
              <span className="ql-formats">
                <button className="ql-list" value="ordered" />
                <button className="ql-list" value="bullet" />
              </span>
              <span className="ql-formats">
                <button className="ql-indent" value="-1" />
                <button className="ql-indent" value="+1" />
                <button className="ql-link" />
              </span>
              <EmailCanvaPicker
                projectId={projectId}
                navigateToUrl={navigateToUrl}
                quillRef={editorInputDescriptionRef}
                openRef={canvaPickerOpenRef}
              />
            </div>

            {/* Editor body */}
            <Editor
              editorInputRef={editorInputDescriptionRef}
              toolbarContainer={toolbarRef}
              showToolbar={true}
              placeholder={t("Start typing... or @ to mention, {{ to show smart values")}
              minRows={12}
              members={members}
              attributes={attributes}
              editorContent={{ htmlContent: eventData.description, contents: {} }}
              onChange={(content) => {
                handleChangeField("description", content.htmlContent);
              }}
              readOnly={false}
              onOpenVariablePicker={() => variablePickerOpenRef.current?.()}
            />
          </div>
          {validationErrors.description && (
            <p className="text-xs text-red-600 mt-1">{validationErrors.description}</p>
          )}
        </div>
      </div>

      {/* ── RIGHT PANE: Recipients sidebar — only shown when Mass sending is ON ── */}
      {massSending && (
        <RecipientsSidebar
          projectId={projectId}
          attendees={eventData.attendees || []}
          itemIds={eventData.itemIds}
          onChangeAttendees={(attendees) => handleChangeField("attendees", attendees)}
          onChangeItemIds={(ids) => handleChangeField("itemIds", ids)}
        />
      )}
    </div>
  );
};

export default EmailForm;
